import { type FC, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TextArea from '../atoms/TextArea';
import TabGroup, { type Tab } from './TabGroup';

export interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  height?: number;
  className?: string;
  id?: string;
}

const tabs: Tab[] = [
  { id: 'edit', label: 'Edit', icon: 'edit' },
  { id: 'preview', label: 'Preview', icon: 'eye' }
];

export const MarkdownEditor: FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your content in Markdown format...\n\n# Heading 1\n## Heading 2\n\n**Bold text** and *italic text*\n\n- Bullet point\n- Another point\n\n```javascript\nconsole.log('Hello world');\n```",
  disabled = false,
  className = '',
  id
}) => {
  const [activeTab, setActiveTab] = useState<string>('edit');

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Tab Navigation */}
      <div className="flex-shrink-0">
        <TabGroup
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          variant="default"
          size="md"
        />
      </div>

      {/* Content Area */}
      <div className="flex-1 h-0 flex flex-col">
        {activeTab === 'edit' ? (
          <div className="flex-1 h-0">
            <TextArea
              id={id}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full h-full font-mono text-sm resize-none p-4"
              autoResize={false}
              style={{ overflow: 'auto', resize: 'none' }}
            />
          </div>
        ) : (
          <div className="flex-1 h-0">
            <div 
              className=" bg-bg-primary p-4 h-full overflow-y-auto"
            >
              {value ? (
                <div className="markdown-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-2xl font-bold text-text-primary mb-4 mt-6 first:mt-0 border-b border-border-light pb-2">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-xl font-semibold text-text-primary mb-3 mt-5 first:mt-0">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-lg font-medium text-text-primary mb-2 mt-4 first:mt-0">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="text-text-primary mb-4 leading-relaxed">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-text-primary">
                          {children}
                        </strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic text-text-secondary">
                          {children}
                        </em>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside mb-4 space-y-1 text-text-primary">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-inside mb-4 space-y-1 text-text-primary">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-text-primary">
                          {children}
                        </li>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-action-key-default bg-bg-surface p-4 mb-4 italic text-text-secondary">
                          {children}
                        </blockquote>
                      ),
                      code: ({ children, className }) => {
                        const match = /language-(\\w+)/.exec(className ?? '');
                        const language = match ? match[1] : '';
                        
                        if (!className) {
                          return (
                            <code className="bg-bg-surface text-text-primary px-1 py-0.5 rounded text-sm font-mono">
                              {children}
                            </code>
                          );
                        }
                        
                        return (
                          <SyntaxHighlighter
                            style={oneDark}
                            language={language}
                            PreTag="div"
                            customStyle={{
                              margin: '1rem 0',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                            }}
                          >
                            {typeof children === 'string' ? children.replace(/\\n$/, '') : String(children).replace(/\\n$/, '')}
                          </SyntaxHighlighter>
                        );
                      },
                      a: ({ children, href }) => (
                        <a 
                          href={href} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-action-key-default hover:text-action-key-darker underline"
                        >
                          {children}
                        </a>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto mb-4">
                          <table className="min-w-full border border-border-light rounded-md">
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-bg-surface">
                          {children}
                        </thead>
                      ),
                      th: ({ children }) => (
                        <th className="border border-border-light px-3 py-2 text-left text-text-primary font-medium">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border border-border-light px-3 py-2 text-text-primary">
                          {children}
                        </td>
                      ),
                      hr: () => (
                        <hr className="border-border-light my-6" />
                      ),
                    }}
                  >
                    {value}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-text-muted italic">No content to preview</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MarkdownEditor.displayName = 'MarkdownEditor';

export default MarkdownEditor;